import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Printer, Download, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const gnf = (v) => `${Math.round(v || 0).toLocaleString('fr-FR')} GNF`;
const thisMonth = () => new Date().toISOString().slice(0, 7);
const lastMonths = () => Array.from({ length: 12 }, (_, i) => {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - i);
  return d.toISOString().slice(0, 7);
});
const monthLabel = (m) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });

const Delta = ({ cur, prev, unit = '', goodWhenUp = true, testId }) => {
  if (cur == null || prev == null) return <span className="text-xs text-gray-400" data-testid={testId}>pas de comparaison</span>;
  const d = Math.round((cur - prev) * 10) / 10;
  const Icon = d > 0 ? TrendingUp : d < 0 ? TrendingDown : Minus;
  const good = d === 0 ? null : (d > 0) === goodWhenUp;
  return (
    <span className={`text-xs flex items-center gap-1 ${good == null ? 'text-gray-500' : good ? 'text-green-700' : 'text-red-600'}`} data-testid={testId}>
      <Icon className="w-3 h-3" /> {d > 0 ? '+' : ''}{d}{unit} vs mois précédent
    </span>
  );
};

const Kpi = ({ label, value, children, testId }) => (
  <div className="border rounded-md p-4" data-testid={testId}>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-2xl font-bold">{value}</p>
    {children}
  </div>
);

const NameList = ({ title, items, render, testId }) => (
  <div data-testid={testId}>
    <p className="text-sm font-medium mb-1">{title}</p>
    {items.length === 0 ? <p className="text-sm text-gray-500">Aucun</p>
      : <ul className="text-sm text-gray-700 space-y-0.5">{items.map((x, i) => <li key={i}>{render(x)}</li>)}</ul>}
  </div>
);

const Summary = ({ d }) => (
  <div className="space-y-6" data-testid="class-summary">
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h2 className="text-xl font-bold">{d.class.name} · {monthLabel(d.month)}</h2>
      <p className="text-sm text-gray-600">{d.class.students} élèves{d.class.main_teacher ? ` · Professeur principal : ${d.class.main_teacher}` : ''}</p>
    </div>
    <Card><CardHeader><CardTitle className="text-base">Résultats scolaires</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Kpi label="Moyenne de classe" value={d.grades.class_average != null ? `${d.grades.class_average} / 20` : '—'} testId="summary-average">
            <Delta cur={d.grades.class_average} prev={d.grades.previous_average} testId="summary-average-delta" />
          </Kpi>
          <Kpi label="Évaluations du mois" value={d.grades.assessments} />
          <Kpi label="Élèves ≥ 14" value={d.grades.distribution.above_14} />
          <Kpi label="Élèves < 10" value={d.grades.distribution.below_10} testId="summary-below10" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <NameList title="Moyennes par matière" items={d.grades.by_subject} render={(s) => `${s.subject} : ${s.average}/20`} testId="summary-subjects" />
          <NameList title="Meilleurs résultats" items={d.grades.top} render={(s) => `${s.name} — ${s.average}/20`} />
          <NameList title="Élèves en difficulté (< 10)" items={d.grades.struggling} render={(s) => `${s.name} — ${s.average}/20`} testId="summary-struggling" />
        </div>
      </CardContent></Card>
    <Card><CardHeader><CardTitle className="text-base">Assiduité</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Kpi label="Taux de présence" value={d.attendance.attendance_rate != null ? `${d.attendance.attendance_rate} %` : '—'} testId="summary-attendance-rate">
            <Delta cur={d.attendance.attendance_rate} prev={d.attendance.previous_rate} unit=" pt" />
          </Kpi>
          <Kpi label="Absences" value={d.attendance.absences} testId="summary-absences">
            <Delta cur={d.attendance.absences} prev={d.attendance.previous_absences} goodWhenUp={false} testId="summary-absences-delta" />
          </Kpi>
          <Kpi label="Retards" value={d.attendance.lates} />
          <Kpi label="Absences excusées" value={d.attendance.excused} />
        </div>
        <NameList title="Absences répétées (3 ou plus)" items={d.attendance.frequent_absentees} render={(s) => `${s.name} — ${s.absences} absence(s)`} testId="summary-absentees" />
      </CardContent></Card>
    <Card><CardHeader><CardTitle className="text-base">Recouvrement (cumul de l’année)</CardTitle></CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Kpi label="Taux de recouvrement" value={d.collection.rate != null ? `${d.collection.rate} %` : '—'} testId="summary-collection-rate" />
        <Kpi label="Encaissé ce mois-ci" value={gnf(d.collection.collected_this_month)} />
        <Kpi label="Reste à recouvrer" value={gnf(d.collection.outstanding)} testId="summary-outstanding" />
        <Kpi label="En retard" value={gnf(d.collection.overdue)}><p className="text-xs text-gray-500">{d.collection.overdue_families} élève(s) concerné(s)</p></Kpi>
      </CardContent></Card>
    <p className="text-xs text-gray-400">Bilan établi le {new Date(d.generated_at).toLocaleString('fr-FR')} par EduScope.</p>
  </div>
);

export default function ClassSummaryPage() {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/reports/class-summary/pdf`, { params: { class_id: classId, month }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Bilan_${data.class.name}_${month}.pdf`.replace(/\s+/g, '_');
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Bilan PDF téléchargé');
    } catch {
      toast.error('Export PDF impossible');
    } finally {
      setDownloading(false);
    }
  };
  useEffect(() => {
    axios.get(`${API}/classes`).then((r) => {
      setClasses(r.data);
      if (r.data.length) setClassId(r.data[0].id);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!classId || !month) return;
    axios.get(`${API}/reports/class-summary`, { params: { class_id: classId, month } })
      .then((r) => setData(r.data)).catch((e) => { setData(null); toast.error(e.response?.data?.detail || 'Bilan indisponible'); });
  }, [classId, month]);

  return (
    <div className="p-6 space-y-6" data-testid="class-summary-page">
      <div className="flex flex-wrap items-end justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-2xl font-bold">Bilan mensuel de classe</h1>
          <p className="text-sm text-gray-600">Résultats, assiduité et recouvrement d’une classe sur un mois, à présenter au conseil d’établissement.</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label>Classe</Label>
            <Select value={classId} onValueChange={setClassId}>
              <SelectTrigger className="w-48" data-testid="summary-class-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1"><Label>Mois</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-44" data-testid="summary-month-input"><SelectValue /></SelectTrigger>
              <SelectContent>{lastMonths().map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
            </Select></div>
          <Button variant="outline" onClick={() => window.print()} disabled={!data} data-testid="summary-print-btn"><Printer className="w-4 h-4 mr-2" /> Imprimer</Button>
          <Button onClick={downloadPdf} disabled={!data || downloading} data-testid="summary-pdf-btn"><Download className="w-4 h-4 mr-2" /> {downloading ? 'Export…' : 'Exporter en PDF'}</Button>
        </div>
      </div>
      {data ? <Summary d={data} /> : <p className="text-gray-500">Choisissez une classe pour afficher le bilan.</p>}
    </div>
  );
}
